import React, { useState } from 'react';
import { STAGES, MONTHS, YEARS } from '../constants/crmConstants';

/**
 * FormModal Component
 * Responsible for creating and editing student progression records.
 * Features:
 * 1. Two-tab interface (Basic Info vs Academic/Progression)
 * 2. Data inheritance handling for new enrollments
 * 3. Delete functionality with confirmation
 */
const FormModal = ({ student, settings, onClose, onSave, onDelete }) => {
  // Default state initialization with support for inherited fields from Evaluation
  const [form, setForm] = useState(student || {
    name: '', 
    email: '', 
    phone: '', 
    sourceAgent: '',
    stage: STAGES[0], 
    twgProgram: '', 
    commencingMonth: '',
    commencingYear: '',
    targetCountry: '',
    destinationUniversity: '', 
    targetProgram: '',
    driveLink: '',
    expectedProgressionMonth: '', 
    expectedProgressionYear: ''
  });
  
  const [activeTab, setActiveTab] = useState('basic');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form, student?.id);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-[2.5rem] w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200" 
        onClick={e => e.stopPropagation()}
      >
        {/* Header Section */}
        <div className="px-10 py-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
          <div>
            <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tighter">
              {student ? 'Edit Student Profile' : 'Register New Student'}
            </h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
              Capture details for the enrollment journey.
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="w-10 h-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all text-xl"
          >
            ✕
          </button>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex px-10 border-b border-slate-100 bg-white">
          <button 
            className={`py-5 px-6 text-[10px] font-black uppercase tracking-[0.15em] border-b-2 transition-all ${
              activeTab === 'basic' ? 'border-[#0464AD] text-[#0464AD]' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`} 
            onClick={() => setActiveTab('basic')}
          >
            1. Basic Information
          </button>
          <button 
            className={`py-5 px-6 text-[10px] font-black uppercase tracking-[0.15em] border-b-2 transition-all ${
              activeTab === 'academic' ? 'border-[#0464AD] text-[#0464AD]' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`} 
            onClick={() => setActiveTab('academic')}
          >
            2. Program & Progression
          </button>
        </div>

        <form className="flex-1 overflow-y-auto" onSubmit={handleSubmit}>
          <div className="p-10 space-y-8">
            {/* TAB 1: BASIC INFO */}
            {activeTab === 'basic' && (
              <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Full Legal Name</label>
                    <input 
                      className="w-full bg-slate-50 border-slate-200 border rounded-2xl p-4 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-[#0464AD] outline-none transition-all" 
                      value={form.name} 
                      onChange={e => setForm({...form, name: e.target.value})} 
                      placeholder="e.g. Rahul Sharma" 
                      required 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                    <input 
                      className="w-full bg-slate-50 border-slate-200 border rounded-2xl p-4 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-[#0464AD] outline-none transition-all" 
                      type="email" 
                      value={form.email} 
                      onChange={e => setForm({...form, email: e.target.value})} 
                      placeholder="rahul@example.com" 
                      required 
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Contact Phone</label>
                    <input 
                      className="w-full bg-slate-50 border-slate-200 border rounded-2xl p-4 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-[#0464AD] outline-none transition-all" 
                      value={form.phone || ''} 
                      onChange={e => setForm({...form, phone: e.target.value})} 
                      placeholder="+91 98765 43210" 
                    />
                  </div>
                  
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Source Agent</label>
                    <select 
                      className="w-full bg-slate-50 border-slate-200 border rounded-2xl p-4 text-sm font-black uppercase text-slate-800 outline-none cursor-pointer" 
                      value={form.sourceAgent} 
                      onChange={e => setForm({...form, sourceAgent: e.target.value})}
                    >
                      <option value="">Select Agent...</option>
                      {(settings.sourceAgents || []).map(a => {
                        const agentName = typeof a === 'string' ? a : a.name;
                        return <option key={agentName} value={agentName}>{agentName}</option>;
                      })}
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Current Pipeline Stage</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-black uppercase text-slate-800 outline-none cursor-pointer" 
                    value={form.stage} 
                    onChange={e => setForm({...form, stage: e.target.value})}
                  >
                    {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* TAB 2: PROGRAM & PROGRESSION */}
            {activeTab === 'academic' && (
              <div className="space-y-8 animate-in fade-in slide-in-from-left-4 duration-300">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">TWG Program Enrolled</label>
                    <select 
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-black uppercase text-slate-800 outline-none cursor-pointer" 
                      value={form.twgProgram} 
                      onChange={e => setForm({...form, twgProgram: e.target.value})}
                    >
                      <option value="">Select Program...</option>
                      {(settings.twgPrograms || []).map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">TWG Program Commencing Term</label>
                    <div className="flex gap-3">
                      <select 
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-black uppercase text-slate-800 outline-none" 
                        value={form.commencingMonth} 
                        onChange={e => setForm({...form, commencingMonth: e.target.value})}
                      >
                        <option value="">Month...</option>
                        {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <select 
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-black uppercase text-slate-800 outline-none" 
                        value={form.commencingYear} 
                        onChange={e => setForm({...form, commencingYear: e.target.value ? parseInt(e.target.value) : ''})}
                      >
                        <option value="">Year...</option>
                        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Target Country</label>
                    <select 
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-black uppercase text-slate-800 outline-none cursor-pointer" 
                      value={form.targetCountry} 
                      onChange={e => setForm({...form, targetCountry: e.target.value})}
                    >
                      <option value="">Select Country...</option>
                      {(settings.countries || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Target University</label>
                    <select 
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-black uppercase text-slate-800 outline-none cursor-pointer" 
                      value={form.destinationUniversity} 
                      onChange={e => setForm({...form, destinationUniversity: e.target.value})}
                    >
                      <option value="">Select University...</option>
                      {(settings.universities || []).map(u => (
                        <option key={u.id || u.name} value={u.name}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Target Program (Destination Course)</label>
                  <input 
                    className="w-full bg-slate-50 border border-slate-200 border rounded-2xl p-4 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-[#0464AD] outline-none transition-all" 
                    value={form.targetProgram || ''} 
                    onChange={e => setForm({...form, targetProgram: e.target.value})} 
                    placeholder="e.g. Master of Computer Science" 
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Expected Progression</label>
                    <div className="flex gap-3">
                      <select 
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-black uppercase text-slate-800 outline-none" 
                        value={form.expectedProgressionMonth} 
                        onChange={e => setForm({...form, expectedProgressionMonth: e.target.value})}
                      >
                        <option value="">Month...</option>
                        {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <select 
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-black uppercase text-slate-800 outline-none" 
                        value={form.expectedProgressionYear} 
                        onChange={e => setForm({...form, expectedProgressionYear: e.target.value ? parseInt(e.target.value) : ''})}
                      >
                        <option value="">Year...</option>
                        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Offer Letter Link (Google Drive)</label>
                    <input 
                      className="w-full bg-slate-50 border border-slate-200 border rounded-2xl p-4 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-[#0464AD] outline-none transition-all" 
                      value={form.driveLink || ''} 
                      onChange={e => setForm({...form, driveLink: e.target.value})} 
                      placeholder="https://drive.google.com/..." 
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="px-10 py-8 flex items-center justify-between border-t border-slate-100 bg-white sticky bottom-0 z-10">
            {student ? (
              <button 
                type="button" 
                onClick={() => window.confirm('Delete student record permanently?') && onDelete(student.id)} 
                className="text-red-500 text-[10px] font-black uppercase tracking-widest hover:underline transition-all"
              >
                Delete Student
              </button>
            ) : <div />}
            <div className="flex gap-4">
              <button 
                type="button" 
                onClick={onClose} 
                className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-10 py-4 bg-[#0464AD] text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-[#0091D0] active:scale-95 transition-all"
              >
                {student ? 'Save Changes' : 'Create Record'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormModal;